import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import ReportTable from "../../components/table/ReportTable";
import sortIcon from "../../assets/icons/sort-icon.svg";
import filterIcon from "../../assets/icons/filter-icon.svg";
import ModifyViewButton from "./ModifyViewButton";
import Loader from "../../components/ui/Loader";
import { useNavigate } from "react-router-dom";
import GTM from "../../GTM";
import Divider from "../../components/ui/Divider";
import useOutsideClick from "../../hooks/useOutsideClick";
import useStickyHeight from "../../hooks/useStickyHeight";
import {
    ACCOMMODATION_REPORT,
    LEAVE_AS_ACCOMMODATION_REPORT,
    paymentHistoryType,
} from "../../constants";
import ReportButtons from "../../components/ui/ReportButtons";
import Button from "../../components/ui/Button";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import HelpSection from "../help/HelpSection";
import ReportSavingPage from "./ReportSavingPage";
import ReportEditPage from "./ReportEditPage";
import DefinitionContent from "../help/DefinitionContent";
import PropTypes from "prop-types";

function ReportTableView({
    handleDropdownSelectedReport,
    dropdownSelectedReport,
    reportDropdownOptions,
    reportTableDisplayInformation,
    ReportTableData,
    filteredDataLength,
    isFetchingReportData,
    getPageCount,
    handlePageSelect,
    getPagePerItems,
    getSortDetails,
    handleColumnFilter,
    columnFilter,
    organizationsAccess,
    reportSortingColumnDirection,
    reportSortingColumnName,
    reportSortingColumnAccessorKey,
    setClearColumnFilter,
    originalDataLength,
    handleExportData,
    handleColumnVisibility,
    columnVisibility,
    handleSsnDisplayMode,
    ssnDisplayMode,
    reportDescription,
    isErrorReport,
    selectedReportDetails,
    dataViewType,
    handleViewReportSave,
    loaderStatus,
    notificationStatus,
    enableSaveButton,
    totalResults,
    retrievedCount,
    isPaginating,
    handleViewReportUpdate,
    isFetchingAccommodation,
    subviewName,
    leavesStatus,
    setEnableSaveButton,
    getColumnLevelFilters,
    setColumnFilter,
    isHighVolumeReport,
}) {
    const [apikey, setApiKey] = useState({});
    const navigate = useNavigate();
    const isFirstSortCheck = useRef(true);
    const saveReportButtonRef = useRef(null);
    const gtm = new GTM(null);
    const firstInteractiveElementRef = useRef(null);
    const [checkColumnOrder, setCheckColumnOrder] = useState([]);
    const [columnResizeMode, setColumnResizeMode] = useState("onChange");
    const [updateViewDetails, setUpdateViewDetails] = useState([]);
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);
    const reportTableSectionRef = useRef(null);
    const stickyFilterBarRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    //Column Definiton popup rendering
    const [openHelp, setOpenHelp] = useState(false);
    const definitionsExplained = useSelector(
        (state) => state?.helpContent?.definitionsExplained
    );
    const [showDefinitionContent, setShowDefinitionContent] = useState(false);
    const [selectedDefinitionContent, setSelectedDefinitionContent] =
        useState(null);
    const [isResetButtonEnabled, setIsResetButtonEnabled] = useState(false);

    const closeHelp = useCallback(() => {
        setOpenHelp(false);
    }, []);

    const handleBack = () => {
        setShowDefinitionContent(false);
        setOpenHelp(true);
    };

    const handleCondition = (condition, value1, value2) => {
        return condition ? value1 : value2;
    };

    const handleClose = useCallback(() => {
        setShowDefinitionContent(false);
        setOpenHelp(false);
    }, []);

    const handleDefinitionCard = (helpCardName) => {
        const selectedContent = definitionsExplained.find(
            (section) => section.Name === helpCardName
        );
        if (selectedContent) {
            setSelectedDefinitionContent(selectedContent);
            setShowDefinitionContent(true);
        }
        //GTM Event
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "column dictionary",
            "report detail",
            dataViewType,
            "column_dictionary_click",
            "undefined"
        );
    };

    const reorderColumn = (draggedColumnId, targetColumnId, columnOrder) => {
        setIsResetButtonEnabled(true);
        setEnableSaveButton(true);
        columnOrder?.splice(
            columnOrder.indexOf(targetColumnId),
            0,
            columnOrder?.splice(columnOrder.indexOf(draggedColumnId), 1)[0]
        );
        return [...columnOrder];
    };

    const [columns, setColumns] = useState([...reportTableDisplayInformation]);

    // Memoize the filter popup rendering
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [isPopupPosition, setIsPopupPosition] = useState({ top: 0, left: 0 });
    const [filterValue, setFilterValue] = useState(null);
    const [accessorKey, setAccessorKey] = useState("");
    const [columnName, setColumnName] = useState("");
    const [columnId, setColumnId] = useState("");
    const popupContainerRef = useRef(null);
    const filterTriggerRef = useRef(null);

    const setFilterTrigger = useCallback((el) => {
        filterTriggerRef.current = el;
    }, []);

    const closePopup = useCallback(() => {
        setPopupVisible(false);
        requestAnimationFrame(() => {
            filterTriggerRef.current?.focus();
        });
    }, []);

    const handleFilterChange = useCallback((e) => {
        e.preventDefault();
        const newValue = e.target.value;
        setFilterValue(newValue);
    }, []);

    const confirmFilter = useCallback(() => {
        handleColumnFilter({
            filterValue: filterValue,
            accessorKey: accessorKey,
            columnName: columnName,
            columnId: columnId,
        });
        setPopupVisible(false);
        handlePageNumberClick(1);
        setEnableSaveButton(true);
        setIsResetButtonEnabled(true);
        requestAnimationFrame(() => {
            filterTriggerRef.current?.focus();
        });
    }, [filterValue, accessorKey, handleColumnFilter]);

    const [sorting, setSorting] = useState({
        id: null,
        desc: false,
        accessorKey: null,
        sortMethod: "",
    });

    const [data, setTableData] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        setTableData(ReportTableData);
    }, [ReportTableData]);

    const [columnOrder, setColumnOrder] = useState(
        columns.map((column) => column.id)
    );

    useEffect(() => {
        setColumns([...reportTableDisplayInformation]);
    }, [reportTableDisplayInformation]);

    useEffect(() => {
        if (reportSortingColumnName?.length > 0) {
            setSorting({
                id: reportSortingColumnName,
                desc: reportSortingColumnDirection,
                accessorKey: reportSortingColumnAccessorKey,
                sortMethod: /(Date|date|Dt|dt)/.test(reportSortingColumnName)
                    ? "date"
                    : "",
            });
            getSortDetails({
                id: reportSortingColumnName,
                desc: reportSortingColumnDirection,
                accessorKey: reportSortingColumnAccessorKey,
                sortMethod: /(Date|date|Dt|dt)/.test(reportSortingColumnName)
                    ? "date"
                    : "",
            });
        }
    }, [reportSortingColumnName, reportSortingColumnDirection]);

    const [showLoader, setShowLoader] = useState(false);
    const [show, setShow] = useState(false);

    const handleItemsPerPageChange = (selectedReport) => {
        getPagePerItems(selectedReport);

        if (sorting && sorting.id && sorting.accessorKey) {
            getSortDetails(sorting);
        }
        setItemsPerPage(selectedReport);
        handlePageSelect(1);
    };
    useEffect(() => {
        handlePageSelect(1);
    }, [isFetchingReportData, dropdownSelectedReport]);
    const handlePageNumberClick = (pageNumber) => {
        // Only call getSortDetails if sorting has valid data
        if (sorting && sorting.id && sorting.accessorKey) {
            getSortDetails(sorting);
        }
        handlePageSelect(pageNumber);
    };
    // Memoized grouped options
    const groupedOptions = useMemo(() => {
        return reportDropdownOptions.reduce((acc, option) => {
            if (!acc[option.ownerType]) acc[option.ownerType] = [];
            acc[option.ownerType].push(option);
            return acc;
        }, {});
    }, [reportDropdownOptions]);
    const table = useReactTable({
        data,
        columns: columns.map((column) => ({
            ...column,
        })),
        state: {
            sorting,
            columnOrder,
            columnVisibility,
        },

        columnResizeMode,
        onColumnVisibilityChange: handleColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
    });

    const getTotalWidth = (columns) => {
        return columns
            .filter((column) => column.getIsVisible())
            .reduce((total, column) => {
                return total + (column.getSize() || 0);
            }, 0);
    };

    const activeColumnWidth = getTotalWidth(table.getAllColumns());

    useEffect(() => {
        if (selectedReportDetails) {
            const getColumnVisibility =
                selectedReportDetails?.TableDisplayInformation
                    ?.ColumnInformation;
            if (getColumnVisibility && getColumnVisibility !== undefined) {
                const hide = {};

                getColumnVisibility?.forEach((item) => {
                    hide[item.id] = item.checked;
                });

                getColumnVisibility?.forEach((item) => {
                    apikey[item.accessorKey] = item.checked;
                });
                setApiKey(apikey);

                handleColumnVisibility(hide);
            }
            setColumnOrder(getColumnVisibility?.map((item) => item?.id));
        }
    }, [selectedReportDetails]);

    const resetColumnOrder = {};
    reportTableDisplayInformation.forEach((item) => {
        resetColumnOrder[item.id] = item.checked;
    });

    const handleResetButton = ({ silent = false } = {}) => {
        setColumnOrder(columns.map((column) => column.id));
        table.resetColumnFilters();
        setColumnFilter(
            getColumnLevelFilters(
                selectedReportDetails?.TableDisplayInformation?.ColumnFilters
            )
        );
        setIsResetButtonEnabled(false);
        setEnableSaveButton(false);
        handleColumnVisibility(resetColumnOrder);
        if (!silent) {
            gtm.Event(
                "custom_event",
                "button",
                "click",
                "reset",
                "report detail",
                dataViewType,
                "field_interaction",
                ""
            );
        }
    };
    const [showEditPage, setShowEditPage] = useState(false);

    const handleSaveButton = () => {
        setShow(true);
        if (
            selectedReportDetails &&
            selectedReportDetails.OwnerType === "Personal"
        ) {
            setShowEditPage(true);
            setUpdateViewDetails(selectedReportDetails);
        } else {
            setShowEditPage(false);
        }
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "save",
            "report detail",
            "selectedViewDataType",
            "field_interaction",
            ""
        );
    };

    const handleReportViewDontSave = () => {
        setShow(false);
    };

    // Generate filter array from current column filters
    const columnFilterValues = useMemo(() => {
        return columnFilter.map((filter) => ({
            columnFilterId: filter.columnId,
            filterValue: filter.filterValue,
        }));
    }, [columnFilter]);

    const handleReportViewSave = async (viewName, viewDescription) => {
        const changedColumnSorting = checkColumnOrder
            .map((id, index) => {
                const matchedObject = sorting.id === id ? sorting : null;
                if (matchedObject) {
                    return {
                        columnDisplayOrderId: index + 1,
                        direction: matchedObject.desc ? "DESC" : "ASC",
                    };
                } else {
                    return null;
                }
            })
            .filter((obj) => obj !== null);
        const changedColumnOrder = checkColumnOrder
            .map((id, index) => {
                const matchedObject = reportTableDisplayInformation.find(
                    (obj) => obj.id === id
                );

                if (matchedObject) {
                    return {
                        displayOrder: index + 1,
                        checked:
                            columnVisibility[id] === undefined
                                ? true
                                : columnVisibility[id],
                        size: matchedObject.size,
                        columnId: matchedObject.columnId,
                    };
                } else {
                    return null;
                }
            })
            .filter((obj) => obj !== null);

        setShow(false);

        handleViewReportSave(
            changedColumnSorting,
            changedColumnOrder,
            viewName,
            viewDescription,
            columnFilterValues,
            setIsResetButtonEnabled
        );
    };

    const handleReportViewUpdate = (viewName, viewDescription, viewId) => {
        const changedColumnSorting = checkColumnOrder
            .map((id, index) => {
                const matchedObject = sorting.id === id;

                if (matchedObject) {
                    return {
                        columnDisplayOrderId: index + 1,
                        direction: matchedObject.desc ? "DESC" : "ASC",
                    };
                } else {
                    return null;
                }
            })
            .filter((obj) => obj !== null);
        const changedColumnOrder = checkColumnOrder
            .map((id, index) => {
                const matchedObject = reportTableDisplayInformation.find(
                    (obj) => obj.id === id
                );
                if (matchedObject) {
                    return {
                        displayOrder: index + 1,
                        checked:
                            columnVisibility[id] === undefined
                                ? true
                                : columnVisibility[id],
                        size: matchedObject.size,
                        columnId: matchedObject.columnId,
                    };
                } else {
                    return null;
                }
            })
            .filter((obj) => obj !== null);
        handleViewReportUpdate(
            changedColumnSorting,
            changedColumnOrder,
            updateViewDetails,
            viewName,
            viewDescription,
            columnFilterValues,
            setIsResetButtonEnabled
        );
        setShow(false);
    };

    useEffect(() => {
        if (loaderStatus === true) {
            setShowLoader(true);
        } else {
            setShowLoader(false);

            // Slight delay to ensure the component is mounted before accessing the ref
            setTimeout(() => {
                saveReportButtonRef.current?.focus();
            }, 200);
        }
    }, [loaderStatus]);

    useEffect(() => {
        if (isDropdownOpen && firstInteractiveElementRef.current) {
            firstInteractiveElementRef.current.focus();
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        const res = {};
        for (const item of reportTableDisplayInformation) {
            const { id, checked } = item;
            if (columnVisibility.hasOwnProperty(id)) {
                if (columnVisibility[id] !== checked) {
                    res[id] = checked;
                }
            }
        }
    }, [columnVisibility]);

    useEffect(() => {
        if (isFirstSortCheck.current || sorting.id === "lastName") {
            isFirstSortCheck.current = false;
            return;
        }
        if (sorting && sorting.id) {
            const label =
                sorting?.desc === true
                    ? "dsc"
                    : sorting?.desc !== null
                      ? "asc"
                      : "";
            const attribute2 = selectedReportDetails?.DataViewType;
            gtm.Event(
                "custom_event",
                "button",
                "click",
                label ? label : "",
                "report detail",
                attribute2 ? attribute2 : "",
                "sort_report",
                sorting.id
            );
        }
        if (sorting && sorting.id && sorting.accessorKey) {
            getSortDetails(sorting);
        }
    }, [sorting]);

    const isClickedOutside = useOutsideClick(dropdownRef, toggleRef); // Toggle dropdown
    const toggleDropdown = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };

    useEffect(() => {
        if (isClickedOutside) {
            setIsDropdownOpen(false);
        }
    }, [isClickedOutside]);

    const availableColumns = table.getAllColumns().map((col) => col.id);
    const columnToUse = handleCondition(
        availableColumns.includes("taxIdentificationNumber"),
        "taxIdentificationNumber",
        "ssn"
    );

    const ssnValues = table
        .getRowModel()
        .rows.map((row) => row.getValue(columnToUse));

    const getSSNDisplay = (cell, index) => {
        const ssnAccessLevels = {
            "No Access": () => {
                if (
                    ["taxIdentificationNumber", "ssn"].includes(cell.column.id)
                ) {
                    return "No Access";
                }
                return flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                );
            },
            Partial: () => {
                if (
                    ["taxIdentificationNumber", "ssn"].includes(cell.column.id)
                ) {
                    if (ssnDisplayMode === 0) {
                        return "***-**-****";
                    } else if (ssnDisplayMode === 1) {
                        return ssnValues[index]?.substr(-4);
                    } else {
                        return "";
                    }
                }
                return flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                );
            },
            Full: () => {
                if (
                    ["taxIdentificationNumber", "ssn"].includes(cell.column.id)
                ) {
                    if (ssnDisplayMode === 0) {
                        return "***-**-****";
                    } else if (ssnDisplayMode === 1) {
                        return ssnValues[index]?.substr(-4);
                    } else if (ssnDisplayMode === 2) {
                        return ssnValues[index];
                    } else {
                        return "";
                    }
                }
                return flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                );
            },
        };

        const displaySSN = ssnAccessLevels[organizationsAccess?.SSNACCESS];

        return displaySSN
            ? displaySSN()
            : flexRender(cell.column.columnDef.cell, cell.getContext());
    };

    // Scrollbar refs and sync logic now handled in ReportTable component

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Re-run when showLoader changes: the stickyFilterBar is only in the DOM
    // when showLoader is false, so the effect must re-attach the observer once
    // the element becomes available.
    useStickyHeight(
        stickyFilterBarRef,
        "--sticky-report-filter-height",
        reportTableSectionRef,
        [showLoader]
    );

    const existingFilter = columnFilter.find(
        (filter) => filter.accessorKey === accessorKey
    );
    const handleKeyDown = (event) => {
        if (event.key === "Escape") {
            closePopup();
        } else if (event.key === "Tab") {
            const focusableElements = Array.from(
                document.querySelectorAll(
                    ".filter-popup-box button, .filter-popup-box input, .filter-popup-box i, .close-area"
                )
            ).filter((el) => !el.disabled);
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (
                !event.shiftKey &&
                document.activeElement === lastElement
            ) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (isPopupVisible) {
                closePopup();
            }
        };

        if (isPopupVisible) {
            window.addEventListener("resize", handleResize);
        }

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isPopupVisible, closePopup]);

    const handleDropDownChange = (value) => {
        setIsDropdownOpen(false);
        handleDropdownSelectedReport(value);
        handleResetButton({ silent: true });
        setItemsPerPage(20);
        getPagePerItems(20);
        setSorting({
            id: reportSortingColumnName,
            desc: reportSortingColumnDirection,
            accessorKey: reportSortingColumnAccessorKey,
            sortMethod: /(Date|date|Dt|dt)/.test(reportSortingColumnName)
                ? "date"
                : "",
        });
        getSortDetails({
            id: reportSortingColumnName,
            desc: reportSortingColumnDirection,
            accessorKey: reportSortingColumnAccessorKey,
            sortMethod: /(Date|date|Dt|dt)/.test(reportSortingColumnName)
                ? "date"
                : "",
        });

        const reportName = (value?.label || "").toLowerCase();
        const isPayment =
            (dataViewType || "").toLowerCase() === "payments" ||
            (dataViewType || "").toLowerCase() ===
                (paymentHistoryType || "").toLowerCase();

        const attribute1 = isPayment
            ? "payment history"
            : `reports ${(dataViewType || "").toLowerCase()}`;

        const attribute2 = (value?.ownerType || "standard").toLowerCase();

        gtm.Event(
            "report detail",
            "button",
            "click",
            reportName,
            attribute1,
            attribute2,
            "dropdown_change_report",
            reportName
        );
    };

    const handleManageReportsClick = () => {
        let data = [];
        navigate(
            `/reportingcapabilities/${
                dataViewType === "Payments" ? paymentHistoryType : dataViewType
            }/manageviews`,
            {
                state: { data },
            }
        );
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "manage my views",
            "report detail",
            dataViewType,
            "navigation_click",
            ""
        );
    };

    const backDrop = true;
    const helpSectionProps = {
        showDefinitionContent,
        selectedDefinitionContent,
        handleBack,
        backDrop,
        setShowDefinitionContent,
        handleHelpClose: handleClose,
    };

    useEffect(() => {
        if (data?.length > 0) {
            const attribute2 = selectedReportDetails?.DataViewType;
            gtm.Event(
                "custom_event",
                "report",
                "population",
                "data population",
                "report detail",
                attribute2 ? attribute2 : "",
                "report_population_success",
                dropdownSelectedReport?.value
            );
        }
    }, [data]);

    useEffect(() => {
        const handleDropdownCloseOnEsc = (e) => {
            if (isDropdownOpen && e.key === "Escape") {
                setIsDropdownOpen(false);
            }
        };

        if (isHighVolumeReport) {
            document.addEventListener("keydown", handleDropdownCloseOnEsc);
        }

        return () => {
            if (isHighVolumeReport) {
                document.removeEventListener(
                    "keydown",
                    handleDropdownCloseOnEsc
                );
            }
        };
    }, [isHighVolumeReport, isDropdownOpen]);

    return (
        <>
            {openHelp && (
                <HelpSection showModal={openHelp} closeHelp={closeHelp} />
            )}
            {showDefinitionContent && selectedDefinitionContent && (
                <DefinitionContent helpSectionProps={helpSectionProps} />
            )}
            {isPopupVisible && ReactDOM.createPortal && (
                <div
                    className="filter-popup-box section-card"
                    role="dialog"
                    aria-labelledby="filter-popup-title"
                    aria-describedby="filter-popup-description"
                    style={{
                        top: isPopupPosition.top,
                        left: isPopupPosition?.left,
                        zIndex: 1000,
                    }}
                    aria-modal="true"
                    onKeyDown={(e) => handleKeyDown(e)}
                >
                    <div className="top-line">
                        <div className="title" id="filter-popup-title">
                            <img src={filterIcon} alt="" />
                            <label>Filter {columnName}</label>
                        </div>

                        <div
                            className="close-area"
                            tabIndex={0}
                            role="button"
                            aria-label="Close filter popup"
                            onClick={closePopup}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                    closePopup();
                            }}
                        >
                            <span className="close-text">Close</span>
                        </div>
                    </div>
                    <div className="filter-box" id="filter-popup-description">
                        <i
                            className="fa fa-search search"
                            aria-hidden="true"
                        ></i>
                        <input
                            type="text"
                            value={filterValue}
                            onChange={(e) => handleFilterChange(e)}
                            placeholder={`Filter`}
                            className="input-box"
                            aria-label={`Filter input for ${columnName}`}
                            autoFocus
                        />
                    </div>
                    <div
                        className="fs d-flex"
                        style={{
                            justifySelf: "end",
                            width: "50%",
                            marginTop: 16,
                        }}
                    >
                        <Button
                            text="Apply"
                            onClick={confirmFilter}
                            disabled={
                                !filterValue.trim() ||
                                existingFilter?.filterValue === filterValue
                            }
                            aria-disabled={!filterValue.trim()}
                        />
                    </div>
                </div>
            )}
            <div
                className="section-card row d-flex "
                style={{ padding: 0, margin: 0 }}
            >
                {showLoader ? (
                    <Loader text="Saving" />
                ) : (
                    <div className="p-0" ref={reportTableSectionRef}>
                        <div
                            className="sticky-filter-bar px-24p"
                            ref={stickyFilterBarRef}
                            style={{
                                paddingTop: 24,
                                borderRadius: 8,
                                display: isFetchingReportData
                                    ? "none"
                                    : "block",
                            }}
                        >
                            <div
                                className="d-flex justify-content-between report-table"
                                style={
                                    isFetchingReportData || isPaginating
                                        ? {
                                              pointerEvents: "none",
                                              opacity: "0.4",
                                          }
                                        : {}
                                }
                            >
                                <div
                                    className="d-flex gap-3"
                                    style={{ flexWrap: "wrap" }}
                                >
                                    {isHighVolumeReport ? (
                                        <div
                                            className="report-view-dropdown"
                                            ref={dropdownRef}
                                            role="combobox"
                                            aria-haspopup="listbox"
                                            aria-expanded={isDropdownOpen}
                                            aria-controls="dropdown-options"
                                            tabIndex={0}
                                            aria-label={
                                                dropdownSelectedReport?.label ||
                                                "Select a report"
                                            }
                                        >
                                            <div
                                                className="d-flex justify-content-between align-items-center cursor-pointer abscenceDropDown"
                                                ref={toggleRef}
                                                onClick={toggleDropdown}
                                                aria-label="Toggle dropdown"
                                            >
                                                <div className="dropdown-header">
                                                    {dropdownSelectedReport?.label ||
                                                        "Select"}
                                                </div>
                                                <i
                                                    className="fa-solid fa-chevron-down"
                                                    style={{ fontSize: 16 }}
                                                    aria-hidden="true"
                                                ></i>
                                            </div>

                                            {isDropdownOpen && (
                                                <div
                                                    className="section-card custom-scrollbar dropdown-container"
                                                    style={{ borderRadius: 4 }}
                                                    tabIndex={-1}
                                                    role="listbox"
                                                >
                                                    {Object.entries(
                                                        groupedOptions
                                                    ).map(
                                                        (
                                                            [
                                                                ownerType,
                                                                options,
                                                            ],
                                                            index
                                                        ) => (
                                                            <div
                                                                key={ownerType}
                                                                className="dropdown-section"
                                                                role="group"
                                                                aria-labelledby={`group-label-${ownerType}`}
                                                            >
                                                                <div
                                                                    className="fw-bold dropdown-group-label"
                                                                    id={`group-label-${ownerType}`}
                                                                >
                                                                    {ownerType}
                                                                </div>
                                                                <ul className="dropdown-options">
                                                                    {options.map(
                                                                        (
                                                                            option
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    option.key
                                                                                }
                                                                                className={
                                                                                    dropdownSelectedReport?.key ===
                                                                                    option.key
                                                                                        ? "selected-option"
                                                                                        : ""
                                                                                }
                                                                                role="option"
                                                                                aria-selected={
                                                                                    dropdownSelectedReport?.value ===
                                                                                    option.value
                                                                                }
                                                                                onClick={() =>
                                                                                    handleDropDownChange(
                                                                                        option
                                                                                    )
                                                                                }
                                                                                onKeyDown={(
                                                                                    e
                                                                                ) =>
                                                                                    e.key ===
                                                                                        "Enter" &&
                                                                                    handleDropDownChange(
                                                                                        option
                                                                                    )
                                                                                }
                                                                                tabIndex={
                                                                                    0
                                                                                }
                                                                            >
                                                                                {
                                                                                    option.label
                                                                                }
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul>
                                                                {index !==
                                                                    Object.keys(
                                                                        groupedOptions
                                                                    ).length -
                                                                        1 && (
                                                                    <Divider
                                                                        className="mb-0p"
                                                                        mx="mt-0p "
                                                                    />
                                                                )}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="section-heading">
                                            {dropdownSelectedReport?.label ||
                                                "Select"}
                                        </div>
                                    )}

                                    <ModifyViewButton
                                        table={table}
                                        attribute2={
                                            selectedReportDetails?.DataViewType
                                        }
                                        setIsResetButtonEnabled={
                                            setIsResetButtonEnabled
                                        }
                                        setEnableSaveButton={
                                            setEnableSaveButton
                                        }
                                    />
                                </div>

                                <div className="d-flex align-items-center gap-3">
                                    <div className="data-view-button w-auto">
                                        <ReportButtons
                                            label="Save"
                                            iconClass="fa-solid fa-floppy-disk pl-8p"
                                            onClick={handleSaveButton}
                                            isDisabled={!enableSaveButton}
                                            ariaLabel="Save the current report view"
                                            ref={saveReportButtonRef}
                                        />
                                    </div>
                                    <div
                                        className="vertical-divider"
                                        style={{ margin: 0 }}
                                    ></div>
                                    <div className="data-view-button w-auto">
                                        <ReportButtons
                                            label="Reset"
                                            iconClass="fa-solid fa-arrow-rotate-left"
                                            onClick={handleResetButton}
                                            isDisabled={!isResetButtonEnabled}
                                            ariaLabel={
                                                "Reset the current report view"
                                            }
                                        />
                                    </div>
                                    <div
                                        className="vertical-divider"
                                        style={{ margin: 0 }}
                                    ></div>
                                    <div className="data-view-button w-auto">
                                        <ReportButtons
                                            label="Manage my reports"
                                            onClick={handleManageReportsClick}
                                            iconClass="fa-solid fa-user-cog"
                                            ariaLabel="Manage my reports"
                                            isDisabled={false}
                                        />
                                    </div>
                                    {originalDataLength > 0 && (
                                        <>
                                            <div
                                                className="vertical-divider"
                                                style={{ margin: 0 }}
                                            ></div>
                                            <div className="data-view-button w-auto">
                                                <ReportButtons
                                                    label="Export data"
                                                    onClick={() =>
                                                        handleExportData(
                                                            columnOrder
                                                        )
                                                    }
                                                    ariaLabel="Export data"
                                                    isDisabled={false}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                }}
                            >
                                <p className="report-header pt-2">
                                    {reportDescription}
                                    {!isFetchingReportData && !isPaginating && (
                                        <>
                                            <strong
                                                style={{ color: "#015294" }}
                                            >
                                                | Total records:{" "}
                                                {originalDataLength.toLocaleString(
                                                    "en-US"
                                                )}
                                            </strong>
                                        </>
                                    )}
                                </p>
                                <div className="data-view-button w-auto">
                                    {definitionsExplained
                                        ?.filter(
                                            (section) =>
                                                section.ContentType ===
                                                "columnDetails"
                                        ) // Filter to only include the section with Order 3
                                        .map((section) => (
                                            <ReportButtons
                                                key={section.Name} // Ensure each item has a unique key
                                                label={section.Name}
                                                onClick={() =>
                                                    handleDefinitionCard(
                                                        section.Name
                                                    )
                                                }
                                                iconClass="fa-solid fa-circle-question"
                                                ariaLabel={section.Name}
                                                isDisabled={false}
                                            />
                                        ))}
                                </div>
                            </div>
                            {isPaginating && (
                                <>
                                    {subviewName !==
                                        LEAVE_AS_ACCOMMODATION_REPORT &&
                                        subviewName !==
                                            ACCOMMODATION_REPORT && (
                                            <div className="total-table">
                                                <div className="spinner"></div>
                                                &nbsp;Retrieving{" "}
                                                {retrievedCount} of{" "}
                                                {totalResults} records.
                                            </div>
                                        )}
                                    {(subviewName ===
                                        LEAVE_AS_ACCOMMODATION_REPORT ||
                                        subviewName === ACCOMMODATION_REPORT) &&
                                        !isFetchingAccommodation && (
                                            <div className="total-table">
                                                <div className="spinner"></div>
                                                &nbsp;Retrieving records.
                                            </div>
                                        )}
                                </>
                            )}
                        </div>

                        {/* Reusable ReportTable Component */}
                        <ReportTable
                            data={data}
                            columns={columns}
                            columnOrder={columnOrder}
                            setColumnOrder={setColumnOrder}
                            columnVisibility={columnVisibility}
                            handleColumnVisibility={handleColumnVisibility}
                            columnResizeMode={columnResizeMode}
                            sorting={sorting}
                            setSorting={setSorting}
                            getSortDetails={getSortDetails}
                            sortIcon={sortIcon}
                            columnFilter={columnFilter}
                            handleColumnFilter={(filters) => {
                                if (
                                    Array.isArray(filters) &&
                                    filters.length === 0
                                ) {
                                    handleColumnFilter([]);
                                } else if (Array.isArray(filters)) {
                                    setClearColumnFilter(filters);
                                } else {
                                    handleColumnFilter(filters);
                                }
                                setIsResetButtonEnabled(true);
                                setEnableSaveButton(true);
                            }}
                            setPopupVisible={setPopupVisible}
                            setAccessorKey={setAccessorKey}
                            setColumnName={setColumnName}
                            setColumnId={setColumnId}
                            setIsPopupPosition={setIsPopupPosition}
                            setFilterValue={setFilterValue}
                            setFilterTrigger={setFilterTrigger}
                            isPopupVisible={isPopupVisible}
                            columnName={columnName}
                            filterValue={filterValue}
                            reorderColumn={reorderColumn}
                            setCheckColumnOrder={setCheckColumnOrder}
                            showPagination={true}
                            pageCount={getPageCount}
                            handlePageNumberClick={handlePageNumberClick}
                            itemsPerPage={itemsPerPage}
                            handleItemsPerPageChange={handleItemsPerPageChange}
                            filteredDataLength={filteredDataLength}
                            organizationsAccess={organizationsAccess}
                            handleSsnDisplayMode={handleSsnDisplayMode}
                            ssnDisplayMode={ssnDisplayMode}
                            ssnValues={ssnValues}
                            getSSNDisplay={getSSNDisplay}
                            isFetchingData={
                                isFetchingAccommodation || isFetchingReportData
                            }
                            isPaginating={isPaginating}
                            renderNoData={
                                !isFetchingReportData &&
                                !isErrorReport &&
                                !leavesStatus &&
                                originalDataLength === 0
                            }
                            isError={isErrorReport}
                        />

                        {(subviewName === LEAVE_AS_ACCOMMODATION_REPORT ||
                        subviewName === ACCOMMODATION_REPORT
                            ? isFetchingAccommodation
                            : isFetchingReportData) && <Loader />}

                        {showEditPage ? (
                            <ReportEditPage
                                onShow={show}
                                onDontSave={handleReportViewDontSave}
                                onSave={handleReportViewUpdate}
                                onSaveAs={handleReportViewSave}
                                notification={notificationStatus}
                                reportDetails={updateViewDetails}
                            />
                        ) : (
                            <ReportSavingPage
                                onShow={show}
                                onDontSave={handleReportViewDontSave}
                                onSave={handleReportViewSave}
                                notification={notificationStatus}
                                reportViewType={dataViewType}
                            />
                        )}
                    </div>
                )}
            </div>{" "}
            <div
                id="aria-live-region"
                style={{ position: "absolute", left: "-9999px" }}
                aria-live="polite"
            ></div>
        </>
    );
}

ReportTableView.propTypes = {
    // Dropdown and report selection
    handleDropdownSelectedReport: PropTypes.func.isRequired,
    dropdownSelectedReport: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        ownerType: PropTypes.string,
    }),
    reportDropdownOptions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            label: PropTypes.string.isRequired,
            ownerType: PropTypes.string.isRequired,
        })
    ).isRequired,
    // Table data and display
    reportTableDisplayInformation: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            header: PropTypes.string,
            accessorKey: PropTypes.string,
            columnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            checked: PropTypes.bool,
            size: PropTypes.number,
            minSize: PropTypes.number,
        })
    ).isRequired,
    ReportTableData: PropTypes.array.isRequired,
    filteredDataLength: PropTypes.number,
    originalDataLength: PropTypes.number.isRequired,
    totalResults: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    retrievedCount: PropTypes.number,
    // Loading states
    isFetchingReportData: PropTypes.bool.isRequired,
    isFetchingAccommodation: PropTypes.bool,
    isPaginating: PropTypes.bool,
    loaderStatus: PropTypes.bool,
    // Pagination
    getPageCount: PropTypes.number.isRequired,
    handlePageSelect: PropTypes.func.isRequired,
    getPagePerItems: PropTypes.func.isRequired,
    // Sorting
    getSortDetails: PropTypes.func.isRequired,
    reportSortingColumnDirection: PropTypes.bool,
    reportSortingColumnName: PropTypes.string,
    reportSortingColumnAccessorKey: PropTypes.string,
    // Filtering
    handleColumnFilter: PropTypes.func.isRequired,
    columnFilter: PropTypes.arrayOf(
        PropTypes.shape({
            columnName: PropTypes.string.isRequired,
            accessorKey: PropTypes.string.isRequired,
            filterValue: PropTypes.string.isRequired,
        })
    ).isRequired,
    setClearColumnFilter: PropTypes.func.isRequired,
    // Column visibility and management
    handleColumnVisibility: PropTypes.func.isRequired,
    columnVisibility: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    // SSN display
    handleSsnDisplayMode: PropTypes.func.isRequired,
    ssnDisplayMode: PropTypes.number.isRequired,
    organizationsAccess: PropTypes.shape({
        SSNACCESS: PropTypes.oneOf(["Full", "Partial", "No Access"]),
    }).isRequired,
    // Report details and description
    reportDescription: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    selectedReportDetails: PropTypes.shape({
        OwnerType: PropTypes.string,
        DataViewType: PropTypes.string,
        TableDisplayInformation: PropTypes.shape({
            ColumnInformation: PropTypes.array,
        }),
    }),
    dataViewType: PropTypes.string.isRequired,
    subviewName: PropTypes.string,
    // Export functionality
    handleExportData: PropTypes.func.isRequired,
    // Error states
    isErrorReport: PropTypes.bool,
    leavesStatus: PropTypes.bool,
    // Save/Update functionality
    handleViewReportSave: PropTypes.func.isRequired,
    handleViewReportUpdate: PropTypes.func.isRequired,
    notificationStatus: PropTypes.func,
    enableSaveButton: PropTypes.bool,
    setEnableSaveButton: PropTypes.func,
    getColumnLevelFilters: PropTypes.func,
    setColumnFilter: PropTypes.func,
    isHighVolumeReport: PropTypes.bool,
};

export default ReportTableView;
